(() => {
  'use strict';
  if (window.RuemoodOutfit) return;
  const sizeKey = value => String(value).trim().toLowerCase().replace(/\s+/g,'');
  const rounded = number => Math.round(number * 10) / 10;
  // Measurements stay in this instance unless the customer opts into size support.
  // Opt-in copies validated measurements only to newly added cart lines, never analytics or browser storage.
  class OutfitFit {
    constructor(outfit) {
      this.outfit = outfit; this.panel = outfit.block.querySelector('[data-outfit-fit]');
      if (!this.panel) return;
      this.unit = 'metric'; this.converted = {};
      this.height = this.panel.querySelector('[data-outfit-fit-height]');
      this.weight = this.panel.querySelector('[data-outfit-fit-weight]');
      this.status = this.panel.querySelector('[data-outfit-fit-status]');
      this.find = this.panel.querySelector('[data-outfit-fit-find]');
      this.clear = this.panel.querySelector('[data-outfit-fit-clear]');
      this.share = this.panel.querySelector('[data-outfit-fit-share]');
      this.privacy = this.panel.querySelector('[data-outfit-fit-privacy]');
      this.unitControls = [...this.panel.querySelectorAll('[data-outfit-fit-unit]')];
      this.entries = [];
      outfit.cards.forEach(card => {
        const body = card.querySelector('.rm-pair__body'), chooser = card.outfitChoice || card.querySelector('[data-pair-choose]');
        if (card.outfitMember.fitNote) {
          const note = document.createElement('p'); note.className = 'rm-outfit-fit__cut'; note.textContent = card.outfitMember.fitNote;
          body.insertBefore(note,chooser);
        }
        const guide = this.readGuide(card), size = card.controls.find(c => /^size$/i.test(c.dataset.optionName));
        if (!guide || !size) return;
        const result = document.createElement('div'); result.className = 'rm-outfit-fit__result'; result.hidden = true;
        const title = document.createElement('strong'), detail = document.createElement('span'), availability = document.createElement('span');
        const actions = document.createElement('div'); actions.className = 'rm-outfit-fit__result-actions';
        const use = document.createElement('button'); use.type = 'button'; use.dataset.outfitFitUse = ''; use.hidden = true;
        const compare = document.createElement('button'); compare.type = 'button'; compare.className = 'rm-outfit-fit__compare'; compare.textContent = 'Check fit';
        actions.append(use,compare); result.append(title,detail,availability,actions); body.insertBefore(result,chooser);
        const entry = {card,guide,size,result,title,detail,availability,use,compare}; this.entries.push(entry);
        outfit.listen(size,'change',() => {
          if (this.applying) return;
          entry.autoPicked = false; card.outfitSuggestedSize = false;
          outfit.sync();
        });
        outfit.listen(use,'click',() => this.apply(entry));
        outfit.listen(compare,'click',() => {
          outfit.open(card,compare);
          if (!card.querySelector('dialog').open) return;
          const sizing = card.querySelector('[data-quick-sizing]');
          if (sizing) { sizing.open = true; sizing.querySelector('summary').focus({preventScroll:true}); sizing.scrollIntoView({block:'start',behavior:'instant'}); }
        });
      });
      if (!this.entries.length) return;
      this.panel.hidden = false;
      this.panel.querySelector('[data-outfit-fit-intro]').textContent = 'See available size suggestions for your measurements. You can edit any size.';
      for (const [key,input] of [['height',this.height],['weight',this.weight]]) {
        outfit.listen(input,'input',() => { delete this.converted[key]; this.profile = null; this.clearAutomaticSizes(); this.status.textContent = ''; input.removeAttribute('aria-invalid'); outfit.sync(); });
        outfit.listen(input,'keydown',event => { if (event.key === 'Enter') { event.preventDefault(); this.calculate(); } });
      }
      outfit.listen(this.find,'click',() => this.calculate());
      outfit.listen(this.share,'change',() => this.sync());
      outfit.listen(this.clear,'click',() => {
        if (this.outfit.busy) return;
        this.height.value = ''; this.weight.value = ''; this.converted = {}; this.profile = null;
        this.clearAutomaticSizes();
        this.share.checked = false;
        this.height.removeAttribute('aria-invalid'); this.weight.removeAttribute('aria-invalid');
        this.status.textContent = this.shared ? 'Cleared from this page. Items already in your bag are unchanged.' : 'Measurements and suggestions cleared.'; outfit.sync(); this.height.focus();
      });
      outfit.listen(this.panel.querySelector('[data-outfit-fit-units]'),'change',event => this.setUnit(event.target.value));
    }
    readGuide(card) {
      try {
        const guide = JSON.parse(card.querySelector('[data-outfit-fit-guide]')?.textContent || 'null');
        if (guide?.schemaVersion !== 1 || guide.approvalStatus !== 'APPROVED' || guide.heightUnit !== 'cm' || guide.weightUnit !== 'kg' || !Array.isArray(guide.rows) || !guide.rows.length) return null;
        if (!guide.rows.every(row => typeof row.size === 'string' && row.size.trim() && ['heightMin','heightMax','weightMin','weightMax'].every(key => typeof row[key] === 'number' && Number.isFinite(row[key]) && row[key] > 0) && row.heightMin <= row.heightMax && row.weightMin <= row.weightMax)) return null;
        return guide;
      } catch (_) { return null; }
    }
    values() {
      const value = (key,input,factor) => this.converted[key]?.display === input.value ? this.converted[key].cmOrKg : Number(input.value) * factor;
      return {height:value('height',this.height,this.unit === 'imperial' ? 2.54 : 1),weight:value('weight',this.weight,this.unit === 'imperial' ? 0.45359237 : 1)};
    }
    setUnit(unit) {
      if (!['metric','imperial'].includes(unit) || unit === this.unit) return;
      const values = this.values(); this.unit = unit;
      this.unitControls.forEach(control => { control.checked = control.value === unit; });
      for (const [key,input,factor] of [['height',this.height,unit === 'imperial' ? 2.54 : 1],['weight',this.weight,unit === 'imperial' ? 0.45359237 : 1]]) {
        if (input.value && Number.isFinite(values[key])) {
          input.value = String(rounded(values[key] / factor));
          // Preserve exact underlying units when only the display changes, including chart boundaries.
          this.converted[key] = {display:input.value,cmOrKg:values[key]};
        }
      }
      this.height.min = unit === 'imperial' ? '19.6' : '50'; this.height.max = unit === 'imperial' ? '98.5' : '250';
      this.weight.min = unit === 'imperial' ? '22' : '10'; this.weight.max = unit === 'imperial' ? '662' : '300';
      this.panel.querySelector('[data-outfit-fit-height-label]').textContent = 'Height (' + (unit === 'imperial' ? 'in' : 'cm') + ')';
      this.panel.querySelector('[data-outfit-fit-weight-label]').textContent = 'Weight (' + (unit === 'imperial' ? 'lb' : 'kg') + ')';
      this.sync();
    }
    validatedValues() {
      const values = this.values();
      const invalidHeight = !this.height.value || !Number.isFinite(values.height) || values.height < 50 || values.height > 250;
      const invalidWeight = !this.weight.value || !Number.isFinite(values.weight) || values.weight < 10 || values.weight > 300;
      if (invalidHeight || invalidWeight) {
        const input = invalidHeight ? this.height : this.weight;
        this.panel.open = true;
        this.status.textContent = 'Enter a valid ' + (invalidHeight ? 'height' : 'weight') + ' in the selected units.';
        input.setAttribute('aria-invalid','true'); input.focus(); return null;
      }
      this.height.removeAttribute('aria-invalid'); this.weight.removeAttribute('aria-invalid');
      return values;
    }
    orderProperties() {
      if (!this.panel || !this.entries?.length || !this.share.checked) return {};
      const values = this.validatedValues();
      if (!values) return null;
      return {_rm_fit_height_cm:String(rounded(values.height)),_rm_fit_weight_kg:String(rounded(values.weight)),_rm_fit_consent:'order-size-support-v1'};
    }
    markShared(items) {
      if (items.some(item => item.fitProperties?._rm_fit_consent)) this.shared = true;
      this.sync();
    }
    calculate() {
      if (this.outfit.busy) return;
      this.profile = null; this.sync();
      const values = this.validatedValues();
      if (!values) return;
      this.profile = values; this.sync();
      this.entries.forEach(entry => {
        if (this.outfit.isActive(entry.card) && !entry.size.value && entry.match) this.apply(entry,{suggested:true});
      });
      const count = this.entries.filter(entry => this.outfit.isActive(entry.card) && entry.matches?.length === 1).length;
      const selected = this.entries.filter(entry => this.outfit.isActive(entry.card) && entry.autoPicked && entry.card.variant?.available).length;
      this.status.textContent = selected ? selected + (selected === 1 ? ' suggested size is' : ' suggested sizes are') + ' selected. Add the piece below, or edit its options.' : count ? 'Review the size suggestions below. Your chosen sizes are unchanged.' : 'Compare the highlighted size guides below before choosing.';
      this.outfit.sync();
    }
    clearAutomaticSizes() {
      this.entries.forEach(entry => {
        if (!entry.autoPicked || this.outfit.inBag.has(entry.card.dataset.pairId)) return;
        entry.autoPicked = false; entry.card.outfitSuggestedSize = false;
        entry.size.value = '';
        entry.size.dispatchEvent(new Event('change',{bubbles:true}));
      });
    }
    matchAvailable(entry) {
      if (!entry.match) return false;
      const index = entry.card.controls.indexOf(entry.size);
      return entry.card.variants.some(variant => variant.available && sizeKey(variant.options[index]) === sizeKey(entry.match.size) && entry.card.controls.every((control,i) => i === index || !control.value || control.value === variant.options[i]));
    }
    apply(entry,{suggested=false} = {}) {
      if (!this.profile || !this.outfit.isActive(entry.card) || this.outfit.busy || this.outfit.inBag.has(entry.card.dataset.pairId) || entry.size.disabled || !this.matchAvailable(entry)) return;
      const option = [...entry.size.options].find(o => sizeKey(o.value) === sizeKey(entry.match.size));
      if (!option || option.disabled) return;
      entry.autoPicked = suggested; entry.card.outfitSuggestedSize = suggested;
      this.applying = true;
      try {
        entry.size.value = option.value;
        entry.size.dispatchEvent(new Event('change',{bubbles:true}));
      } finally { this.applying = false; }
      this.status.textContent = entry.match.size + ' selected for ' + (entry.card.outfitMember.displayName || entry.card.querySelector('.rm-pair__title').textContent.trim()) + '. You can change it in the product options.';
      this.sync();
    }
    sync() {
      if (!this.panel || !this.entries?.length) return;
      const activeEntries = this.entries.filter(entry => this.outfit.isActive(entry.card));
      this.panel.hidden = !activeEntries.length && !this.height.value && !this.weight.value;
      this.find.disabled = !!this.outfit.busy || !activeEntries.length;
      this.panel.querySelector('[data-outfit-fit-intro]').textContent = activeEntries.length ? 'See available size suggestions for your measurements. You can edit any size.' : 'Choose sizes in each product’s size guide. You can still share your measurements for size support.';
      [this.height,this.weight,this.share,this.clear,...this.unitControls].forEach(control => { control.disabled = !!this.outfit.busy; });
      this.clear.hidden = !this.height.value && !this.weight.value;
      this.privacy.textContent = this.shared ? 'Already shared with items in your bag. Changes here apply to new items.' : this.share.checked ? 'Saved with new items you add, for size support.' : 'Used on this page unless you choose to share.';
      this.entries.forEach(entry => {
        entry.result.hidden = !this.profile || !this.outfit.isActive(entry.card); entry.use.hidden = true; entry.match = null; entry.matches = [];
        if (!this.profile || !this.outfit.isActive(entry.card)) return;
        const sizes = [...entry.size.options].map(option => sizeKey(option.value));
        const matches = entry.guide.rows.filter(row => sizes.includes(sizeKey(row.size)) && this.profile.height >= row.heightMin && this.profile.height <= row.heightMax && this.profile.weight >= row.weightMin && this.profile.weight <= row.weightMax);
        entry.matches = [...new Map(matches.map(row => [sizeKey(row.size),row])).values()];
        entry.detail.textContent = ''; entry.availability.textContent = '';
        entry.compare.disabled = !!this.outfit.busy;
        if (entry.matches.length !== 1) {
          entry.title.textContent = entry.matches.length ? 'Between ' + entry.matches.map(row => row.size).join(' and ') : 'Compare measurements';
          entry.detail.textContent = entry.matches.length ? 'Both sizes appear in the height-and-weight guide. Compare their measurements.' : 'Use the garment measurements to choose the fit you prefer.';
          entry.compare.textContent = 'Compare sizes'; return;
        }
        entry.match = entry.matches[0]; entry.title.textContent = 'Suggested size: ' + entry.match.size;
        const chart = entry.card.quickshop?.chart, row = chart?.rows?.find(row => sizeKey(row.size) === sizeKey(entry.match.size));
        const dimensions = [];
        if (row) for (const [key,label] of [['chest','chest'],['length','length']]) {
          let value = Number(row.values?.[key]);
          if (!Number.isFinite(value) || value <= 0) continue;
          if (key === 'chest' && chart.basis === 'garment_flat') value *= 2;
          dimensions.push(label + ' ' + rounded(value / (this.unit === 'imperial' ? 2.54 : 1)) + (this.unit === 'imperial' ? ' in' : ' cm'));
        }
        entry.detail.textContent = dimensions.length ? 'Garment ' + dimensions.join(' · ') : 'From this piece’s height-and-weight guide.';
        const available = this.matchAvailable(entry), selected = sizeKey(entry.size.value) === sizeKey(entry.match.size), inBag = this.outfit.inBag.has(entry.card.dataset.pairId);
        entry.use.hidden = selected; entry.use.textContent = selected ? entry.match.size + ' selected' : 'Use ' + entry.match.size;
        entry.use.disabled = !available || selected || inBag || !!this.outfit.busy || entry.size.disabled;
        entry.compare.textContent = 'Check fit';
        if (!available) entry.availability.textContent = 'This size is unavailable with the chosen options.';
        else if (inBag) entry.availability.textContent = 'This piece is already in your bag.';
      });
    }
  }
  class OutfitLooks {
    constructor(outfit) {
      this.outfit = outfit; this.block = outfit.block;
      this.controls = this.block.querySelector('[data-outfit-looks]');
      this.looks = outfit.config.looksVersion === 1 ? outfit.config.looks : null;
      if (!this.controls || !Array.isArray(this.looks) || this.looks.length < 2) return;
      this.tabs = [...this.controls.querySelectorAll('[data-outfit-look]')];
      this.image = this.block.querySelector('.rm-outfit__image');
      this.panel = this.block.querySelector('[data-outfit-layout]');
      this.status = this.controls.querySelector('[data-look-status]');
      this.saved = new Map();
      this.active = this.looks[0];
      this.tabs.forEach(tab => {
        outfit.listen(tab,'click',() => this.change(tab.dataset.outfitLook));
        outfit.listen(tab,'keydown',event => {
          if (!['ArrowLeft','ArrowRight','Home','End'].includes(event.key)) return;
          event.preventDefault();
          const enabled = this.tabs.filter(t => !t.disabled), index = enabled.indexOf(tab);
          const next = event.key === 'Home' ? 0 : event.key === 'End' ? enabled.length - 1 : (index + (event.key === 'ArrowRight' ? 1 : -1) + enabled.length) % enabled.length;
          enabled.forEach((t,i) => { t.tabIndex = i === next ? 0 : -1; });
          enabled[next]?.focus();
        });
      });
      this.apply(this.active); this.controls.hidden = false;
    }
    available(look) {
      return look.members.filter(m => m.default).every(member => {
        const card = this.outfit.cards.find(c => c.dataset.pairId === String(member.id));
        const colorIndex = card?.controls.findIndex(c => /^(color|colour|design)$/i.test(c.dataset.optionName));
        return card?.variants.some(v => v.available && (!member.color || colorIndex < 0 || v.options[colorIndex] === member.color));
      });
    }
    activeCard(card) { return !this.active || this.active.members.some(m => String(m.id) === card.dataset.pairId); }
    save() {
      this.saved.set(this.active.id, {
        selected: new Set(this.outfit.selected),
        options: new Map(this.outfit.cards.filter(c => this.activeCard(c) && !c.hasAttribute('data-outfit-source')).map(c => [c.dataset.pairId,{values:c.controls.map(v => v.value),suggested:!!c.outfitSuggestedSize}]))
      });
    }
    preload(url) {
      return new Promise((resolve,reject) => {
        const image = new Image(); let done = false;
        const finish = error => { if (done) return; done = true; clearTimeout(timer); image.onload = null; image.onerror = null; this.outfit.abort.signal.removeEventListener('abort',cancel); error ? reject(error) : resolve(); };
        const cancel = () => finish(new Error('Cancelled'));
        const timer = setTimeout(() => finish(new Error('Image timed out')),15000);
        this.outfit.abort.signal.addEventListener('abort',cancel,{once:true});
        image.onload = () => finish(); image.onerror = () => finish(new Error('Image unavailable')); image.src = url;
      });
    }
    async change(id) {
      const o = this.outfit, look = this.looks?.find(l => l.id === id);
      if (!look || look === this.active || this.loading || o.busy || o.uncertain || this.block.querySelector('dialog[open]') || !this.available(look)) return;
      this.loading = true; this.status.textContent = 'Loading ' + look.name.toLowerCase() + '…'; o.sync();
      try {
        const tab = this.tabs.find(t => t.dataset.outfitLook === id);
        await this.preload(tab.dataset.lookImage);
        if (o.abort.signal.aborted) return;
        this.save(); this.apply(look);
        this.image.src = tab.dataset.lookImage; this.image.alt = look.alt || o.config.alt;
        this.image.width = look.width || 1122; this.image.height = look.height || 1402;
        this.status.textContent = look.name + ' selected.';
        o.status.textContent = ''; o.guided = false;
      } catch (_) {
        if (!o.abort.signal.aborted) this.status.textContent = 'This look could not load. Your current selections are safe. Please try again.';
      } finally {
        this.loading = false;
        if (!o.abort.signal.aborted) { o.sync(); this.block.observe(); }
      }
    }
    apply(look) {
      const o = this.outfit, saved = this.saved.get(look.id);
      this.active = look; this.block.dataset.lookId = look.id;
      o.selected = new Set(saved?.selected || look.members.filter(m => m.default).map(m => String(m.id)));
      o.cards.forEach(card => {
        const member = look.members.find(m => String(m.id) === card.dataset.pairId);
        card.hidden = !member;
        if (!member) { o.selected.delete(card.dataset.pairId); return; }
        card.outfitMember = member;
        card.style.order = String(look.members.indexOf(member));
        card.dataset.position = String(look.members.indexOf(member));
        if (saved?.options.has(card.dataset.pairId)) {
          const previous = saved.options.get(card.dataset.pairId);
          card.controls.forEach((control,i) => { control.value = previous.values[i]; });
          card.outfitSuggestedSize = previous.suggested;
          const entry = o.fitHelper.entries.find(e => e.card === card); if (entry) entry.autoPicked = previous.suggested;
          o.fitHelper.entries.find(e => e.card === card)?.result.setAttribute('hidden','');
        }
        this.block.update(card);
        const hotspot = this.block.querySelector('[data-outfit-hotspot="' + card.dataset.pairId + '"]');
        if (hotspot && Number.isFinite(member.x) && Number.isFinite(member.y)) {
          hotspot.style.setProperty('--x',member.x + '%'); hotspot.style.setProperty('--anchor-x',member.x + 'cqw'); hotspot.style.setProperty('--y',member.y + '%');
          hotspot.classList.toggle('rm-outfit__hotspot--left',member.labelSide === 'left');
          hotspot.classList.toggle('rm-outfit__hotspot--below',member.calloutPlacement === 'below');
          hotspot.setAttribute('aria-label','Choose ' + card.querySelector('.rm-pair__title').textContent.trim());
          hotspot.querySelector('.rm-outfit__hotspot-name').textContent = member.calloutName || member.displayName || member.label;
          const type = hotspot.querySelector('.rm-outfit__hotspot-type'); if (type) type.textContent = member.calloutType || '';
        }
      });
      const list = this.block.querySelector('.rm-outfit__items');
      look.members.forEach(member => { const card = o.cards.find(c => c.dataset.pairId === String(member.id)); if (card) list.append(card); });
      this.block.querySelectorAll('[data-outfit-hotspot]').forEach(button => {
        const member = look.members.find(m => String(m.id) === button.dataset.outfitHotspot);
        button.hidden = !member || !Number.isFinite(member.x) || !Number.isFinite(member.y) || !o.cards.some(c => c.dataset.pairId === String(member.id));
      });
      this.panel.setAttribute('role','tabpanel');
      this.panel.setAttribute('aria-labelledby',this.tabs.find(t => t.dataset.outfitLook === look.id).id);
      o.fitHelper.status.textContent = '';
      this.sync();
    }
    sync() {
      if (!this.active) return;
      const locked = this.loading || this.outfit.busy || this.outfit.uncertain;
      this.panel.setAttribute('aria-busy',String(!!this.loading));
      this.tabs.forEach(tab => {
        const selected = tab.dataset.outfitLook === this.active.id;
        tab.setAttribute('aria-selected',String(selected)); tab.tabIndex = selected ? 0 : -1;
        tab.disabled = !!locked || !this.available(this.looks.find(l => l.id === tab.dataset.outfitLook));
      });
    }
  }
  window.RuemoodOutfit = class {
    constructor(block, api) {
      this.block = block; this.api = api; this.cards = block.cards;
      this.config = JSON.parse(block.querySelector('[data-outfit-config]').textContent);
      this.abort = new AbortController(); this.selected = new Set(); this.inBag = new Set();
      this.button = block.querySelector('[data-outfit-add]');
      this.status = block.querySelector('[data-outfit-status]');
      this.bag = block.querySelector('[data-outfit-bag]');
      this.listen = (el, event, fn) => el.addEventListener(event, fn, {signal:this.abort.signal});
      this.cards.forEach(card => {
        const id = card.dataset.pairId;
        card.outfitMember = this.config.members.find(m => String(m.id) === id) || {};
        if (card.outfitMember.default && card.variants.some(v=>v.available)) this.selected.add(id);
        const label = document.createElement('label'); label.className = 'rm-outfit__include';
        const check = document.createElement('input'); check.type = 'checkbox';
        check.setAttribute('aria-label','Include ' + card.querySelector('.rm-pair__title').textContent.trim());
        label.append(check); card.append(label); card.outfitCheck = check;
        const note = document.createElement('span'); note.className = 'rm-outfit__item-note';
        card.querySelector('.rm-pair__body').append(note); card.outfitNote = note;
        const feedback = document.createElement('p'); feedback.className = 'rm-outfit__feedback'; feedback.setAttribute('role','status');
        card.querySelector('.rm-pair__body').append(feedback); card.outfitFeedback = feedback;
        card.querySelector('[data-pair-choose] > span')?.remove();
        const choice = document.createElement('div'); choice.className = 'rm-outfit__choice'; choice.hidden = true;
        const selected = document.createElement('span');
        const edit = document.createElement('button'); edit.type = 'button'; edit.className = 'rm-outfit__edit'; edit.textContent = 'Edit';
        edit.setAttribute('aria-label','Edit size and colour for ' + card.querySelector('.rm-pair__title').textContent.trim());
        edit.setAttribute('aria-haspopup','dialog'); edit.setAttribute('aria-controls',card.querySelector('dialog').id); edit.setAttribute('aria-expanded','false');
        choice.append(selected,edit);
        card.querySelector('.rm-pair__body').insertBefore(choice,card.querySelector('[data-pair-choose]'));
        card.outfitChoice = choice; card.outfitChoiceText = selected; card.outfitEdit = edit;
        this.listen(edit,'click',() => { this.guided = false; this.open(card,edit); });
        const save = document.createElement('button'); save.type = 'button';
        save.className = 'rm-outfit__save'; save.textContent = 'Use these options';
        card.querySelector('.rm-pair__actions').append(save); card.outfitSave = save;
        this.listen(check,'change',() => { this.select(card,check.checked); this.guided = false; this.status.textContent = ''; this.sync(); });
        this.listen(card.querySelector('[data-quick-close]'),'click',() => { this.guided = false; });
        this.listen(card.querySelector('dialog'),'cancel',() => { this.guided = false; });
        this.listen(save,'click',() => {
          if (this.busy || !card.variant?.available || this.inBag.has(id)) return;
          this.select(card,true); card.quickshop.close(); this.sync();
          if (this.guided) {
            const next = this.pending().find(c => !c.variant?.available);
            if (next) this.open(next,this.button);
            else { this.guided = false; this.add(); }
          }
        });
      });
      block.querySelectorAll('[data-outfit-hotspot]').forEach(button => {
        const card = this.cards.find(c => c.dataset.pairId === button.dataset.outfitHotspot);
        if (!card?.querySelector('.rm-quickshop')) return;
        button.hidden = false; button.setAttribute('aria-haspopup','dialog'); button.setAttribute('aria-controls',card.querySelector('dialog').id);
        this.listen(button,'click',() => { this.guided = false; this.open(card,button); });
      });
      this.fitHelper = new OutfitFit(this);
      this.lookSwitcher = new OutfitLooks(this);
      this.listen(this.button,'click',() => this.add());
      this.listen(this.bag,'click',() => {
        if (window.cartCoreV2?.openDrawer) window.cartCoreV2.openDrawer();
        else location.href = this.api.root + 'cart';
      });
      this.onCart = () => this.api.readCart().then(cart => this.reconcile(cart.items)).catch(() => {});
      this.listen(document,'ruemood:pairing-cart-updated',this.onCart);
      this.onCore = state => { if (Array.isArray(state?.cart?.items)) this.reconcile(state.cart.items); else this.onCart(); };
      window.cartCoreV2?.on?.('cart:state-changed',this.onCore);
      block.querySelector('[data-outfit-checkout]').hidden = false;
      this.sync();
    }
    destroy() { this.abort.abort(); window.cartCoreV2?.off?.('cart:state-changed',this.onCore); }
    select(card,include) {
      if (include) {
        const group = card.outfitMember.group;
        if (group) this.cards.filter(c => c !== card && c.outfitMember.group === group).forEach(c => this.selected.delete(c.dataset.pairId));
        this.selected.add(card.dataset.pairId);
      } else this.selected.delete(card.dataset.pairId);
    }
    isActive(card) { return !this.lookSwitcher || this.lookSwitcher.activeCard(card); }
    pending() { return this.cards.filter(c => this.isActive(c) && this.selected.has(c.dataset.pairId) && !this.inBag.has(c.dataset.pairId)).sort((a,b) => Number(a.dataset.position) - Number(b.dataset.position)); }
    open(card,trigger) { if (!this.busy && !this.lookSwitcher?.loading && this.isActive(card)) { this.block.openOptions(card,trigger); this.sync(); } }
    chooseOrAdd(card,trigger) {
      if (this.busy || this.lookSwitcher?.loading || !this.isActive(card) || this.uncertain || this.inBag.has(card.dataset.pairId) || !card.variants.some(v=>v.available)) return;
      this.guided = false;
      if (!card.variant?.available) { this.open(card,trigger); return; }
      this.select(card,true);
      return this.add([card]);
    }
    reconcile(items) {
      this.inBag = new Set(items.map(i => String(i.product_id)));
      this.cards.forEach(card => { card.hidden = !this.isActive(card); }); this.block.hidden = false;
      this.sync();
    }
    money(cents) {
      const currency = this.block.dataset.currency;
      try { return new Intl.NumberFormat(document.documentElement.lang || 'en',{style:'currency',currency}).format(cents/100) + ' ' + currency; }
      catch (_) { return (cents/100).toFixed(2) + ' ' + currency; }
    }
    confirmedAfter(attempted,before,after) {
      return attempted.filter(({variant}) => {
        const qty = items => items.filter(i=>String(i.variant_id || i.id)===String(variant.id)).reduce((sum,i)=>sum+Number(i.quantity||0),0);
        return qty(after.items) > qty(before.items);
      });
    }
    sync() {
      let total = 0, estimate = false;
      const pending = this.pending();
      this.cards.forEach(card => {
        const id = card.dataset.pairId, inBag = this.inBag.has(id);
        if (inBag) card.outfitFeedback.textContent = '';
        const checked = this.isActive(card) && this.selected.has(id);
        card.outfitCheck.checked = checked;
        const soldOut = !card.variants.some(v=>v.available);
        card.outfitCheck.disabled = (!!this.busy || !!this.lookSwitcher?.loading) || inBag || soldOut;
        card.outfitSave.disabled = (!!this.busy || !!this.lookSwitcher?.loading) || inBag || !card.variant?.available;
        const furtherChoices = pending.some(other => other !== card && !other.variant?.available);
        card.outfitSave.textContent = inBag ? 'Already in your bag' : this.guided ? (furtherChoices ? 'Continue' : this.addLabel(pending.length)) : 'Use these options';
        card.outfitNote.textContent = inBag ? 'Already in your bag · not added again' : soldOut ? 'Sold out' : card.outfitMember.default === false ? (card.outfitMember.alternativeLabel || 'Alternative to the black pants') : card.hasAttribute('data-outfit-source') ? 'The piece you are viewing' : '';
        const chooser = card.querySelector('[data-pair-choose]');
        const chosen = card.controls.map(c => c.value).filter(Boolean);
        const ready = !!card.variant?.available;
        const missing = card.controls.filter(c => !c.value).map(c => /^(color|colour)$/i.test(c.dataset.optionName) ? 'colour' : c.dataset.optionName.toLowerCase());
        const action = missing.length === 1 ? 'Choose ' + missing[0] : 'Choose options';
        chooser.firstChild.textContent = inBag ? 'In your bag' : soldOut ? 'Sold out' : this.busy && this.addingIds?.has(id) ? 'Adding…' : ready ? 'Add to bag' : action;
        chooser.disabled = (!!this.busy || !!this.lookSwitcher?.loading) || !!this.uncertain || inBag || soldOut;
        chooser.classList.toggle('rm-outfit__ready',ready && !inBag);
        chooser.setAttribute('aria-label',chooser.firstChild.textContent + ': ' + card.querySelector('.rm-pair__title').textContent.trim() + (ready && chosen.length ? ', ' + chosen.join(', ') : ''));
        if (ready || inBag || soldOut) {
          chooser.removeAttribute('aria-haspopup'); chooser.removeAttribute('aria-controls'); chooser.removeAttribute('aria-expanded');
        } else {
          chooser.setAttribute('aria-haspopup','dialog'); chooser.setAttribute('aria-controls',card.querySelector('dialog').id);
        }
        card.outfitChoice.hidden = !ready || inBag;
        card.outfitChoiceText.textContent = (card.outfitSuggestedSize ? 'Suggested: ' : 'Selected: ') + (chosen.join(' · ') || 'One option');
        card.outfitEdit.disabled = (!!this.busy || !!this.lookSwitcher?.loading) || !!this.uncertain || inBag;
        if (checked && !inBag) {
          const possible = card.variants.filter(v => v.available && card.controls.every((c,i) => !c.value || c.value === v.options[i]));
          total += card.variant?.available ? card.variant.price : possible.length ? Math.min(...possible.map(v=>v.price)) : 0;
          estimate ||= !card.variant?.available && new Set(possible.map(v => v.price)).size > 1;
        }
      });
      this.block.querySelector('[data-outfit-count]').textContent = pending.length + (pending.length === 1 ? ' piece selected' : ' pieces selected');
      this.block.querySelector('[data-outfit-total]').textContent = (estimate ? 'From ' : '') + this.money(total);
      const missing = pending.filter(c => !c.variant?.available).length;
      this.button.disabled = (!!this.busy || !!this.lookSwitcher?.loading) || (!this.uncertain && pending.length === 0);
      this.button.textContent = this.busy ? 'Updating your bag…' : this.uncertain ? 'Check bag status' : !pending.length ? 'Select a piece to add' : this.addLabel(pending.length);
      this.block.querySelector('[data-outfit-help]').textContent = pending.length ? (missing ? 'We’ll confirm any missing sizes or colours before adding. ' : '') + 'Shipping and any discounts are calculated at checkout.' : 'Pieces already in your bag are excluded from this total.';
      this.fitHelper?.sync();
      this.lookSwitcher?.sync();
    }
    addLabel(count) { return 'Add ' + count + (count === 1 ? ' piece' : ' pieces') + ' to bag'; }
    async add(requestedCards = null) {
      if (this.busy || this.lookSwitcher?.loading) return;
      const requestedIds = requestedCards && new Set(requestedCards.map(card => card.dataset.pairId));
      const pendingForRequest = () => this.pending().filter(card => !requestedIds || requestedIds.has(card.dataset.pairId));
      if (this.uncertain) {
        this.busy = true; this.sync();
        try {
          const cart = await this.api.readCart();
          if (this.unconfirmed) {
            const recovered = this.confirmedAfter(this.unconfirmed.attempted,this.unconfirmed.before,cart);
            this.fitHelper.markShared(recovered);
            recovered.forEach(({card,variant})=>this.api.publish('add',this.block,card,variant,{quantity:1}));
          }
          this.unconfirmed = null; this.uncertain = false; this.reconcile(cart.items);
          this.status.textContent = 'Bag checked. Review the remaining pieces before adding.';
        }
        catch (_) { this.status.textContent = 'We still cannot confirm your bag. Please view your bag before adding again.'; }
        finally { this.busy = false; this.sync(); }
        return;
      }
      let pending = pendingForRequest();
      if (!pending.length) return;
      // The outfit action can use entered measurements without another Find size click.
      // Only approved guides can fill an empty size; deliberate choices are preserved.
      if (!requestedCards && this.fitHelper.height?.value && this.fitHelper.weight?.value && this.fitHelper.entries?.some(entry => pending.includes(entry.card) && !entry.size.value)) {
        this.fitHelper.calculate();
        if (!this.fitHelper.profile) return;
        pending = pendingForRequest();
      }
      const missing = pending.find(c => !c.variant?.available);
      if (missing) { this.guided = true; this.open(missing,this.button); return; }
      const fitProperties = this.fitHelper.orderProperties();
      if (fitProperties === null) { this.status.textContent = 'Check your measurements, or turn off sharing to continue.'; return; }
      pending.forEach(card => { card.outfitFeedback.textContent = ''; });
      this.busy = true; this.addingIds = new Set(pending.map(card => card.dataset.pairId)); this.status.textContent = ''; this.sync();
      let before, attempted = [], confirmed = [], submitted = false, failure;
      try {
        before = await this.api.readCart(); this.reconcile(before.items);
        pending = pendingForRequest();
        if (!pending.length) { this.status.textContent = 'These pieces are already in your bag.'; this.bag.hidden = false; return; }
        attempted = pending.map(card => ({card,variant:card.variant,fitProperties}));
        const items = attempted.map(({card,variant}) => {
          const properties = {...fitProperties,...(this.api.qa ? {_rm_pairing_qa:'1'} : {})};
          if (!card.hasAttribute('data-outfit-source') && this.api.consent()) Object.assign(properties,{
            _rm_pairing_source:this.block.dataset.source,_rm_pairing_version:this.block.dataset.version,
            _rm_pairing_placement:this.block.dataset.placement,_rm_pairing_position:card.dataset.position,
            ...(this.block.dataset.lookId ? {_rm_outfit_look:this.block.dataset.lookId} : {})
          });
          return {id:variant.id,quantity:1,properties};
        });
        submitted = true;
        const response = await fetch(this.api.root + 'cart/add.js',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({items})});
        const result = await response.json();
        if (!response.ok) throw new Error(typeof result.description === 'string' ? result.description : 'Some options are no longer available.');
        // Confirm each returned variant; never treat an incomplete response as a full add.
        const returned = result.items || [];
        confirmed = attempted.filter(({variant}) => returned.some(i => String(i.variant_id || i.id) === String(variant.id)));
        if (confirmed.length !== attempted.length) throw new Error('We could not confirm every selected piece.');
      } catch (error) { failure = error; }
      finally {
        if (submitted) {
          try {
            const after = await this.api.readCart();
            // An error can follow a partial server-side add. Check real quantities before offering a retry.
            if (failure) confirmed = this.confirmedAfter(attempted,before,after);
            this.reconcile(after.items);
          } catch (_) {
            confirmed.forEach(({card}) => this.inBag.add(card.dataset.pairId));
            this.uncertain = !!failure;
            if (this.uncertain) this.unconfirmed = {attempted:attempted.filter(x=>!confirmed.includes(x)),before};
          }
          confirmed.forEach(({card,variant}) => this.api.publish('add',this.block,card,variant,{quantity:1}));
          this.fitHelper.markShared(confirmed);
          const n = confirmed.length;
          this.status.textContent = this.uncertain ? 'We could not confirm the update. Check your bag before adding again.' : failure ? (n ? n + (n===1?' piece was':' pieces were') + ' added. ' : '') + (failure.message || 'The remaining pieces could not be added.') : n + (n===1?' piece added':' pieces added') + ' to your bag.';
          this.bag.hidden = false;
          if (failure) attempted.filter(x=>!confirmed.includes(x)).forEach(({card,variant})=>this.api.publish('error',this.block,card,variant,{error_type:'outfit_add_failed'}));
          await window.cartCoreV2?.loadCartState?.().catch(()=>{});
          document.dispatchEvent(new CustomEvent('ruemood:pairing-cart-updated'));
        } else if (failure) this.status.textContent = 'Your bag could not be checked. Nothing was added. Please try again.';
        if (requestedCards?.length === 1 && failure && !this.inBag.has(requestedCards[0].dataset.pairId)) requestedCards[0].outfitFeedback.textContent = this.status.textContent;
        this.busy = false; this.addingIds = null; this.sync();
      }
    }
  };
})();
